/**
 * Grid layout helpers for procedural city generation.
 */

/** @param {import('../../config.js').CONFIG['city']} config */
export function getCitySpan(config) {
  return config.roadWidth + config.blockCount * (config.lotSize + config.roadWidth);
}

/** @param {import('../../config.js').CONFIG['city']} config */
export function getCityOffset(config) {
  return -getCitySpan(config) / 2;
}

export function combineSeed(citySeed, gx, gz, salt = 0) {
  return (citySeed + gx * 73856093 + gz * 19349663 + salt * 83492791) >>> 0;
}

/** @param {import('../../config.js').CONFIG['city']} config */
export function lotWorldRect(gx, gz, config) {
  const offset = getCityOffset(config);
  const minX = offset + config.roadWidth + gx * (config.lotSize + config.roadWidth);
  const minZ = offset + config.roadWidth + gz * (config.lotSize + config.roadWidth);
  const maxX = minX + config.lotSize;
  const maxZ = minZ + config.lotSize;
  return {
    minX,
    minZ,
    maxX,
    maxZ,
    centerX: (minX + maxX) / 2,
    centerZ: (minZ + maxZ) / 2,
    width: config.lotSize,
    depth: config.lotSize,
  };
}

/** Vertical road strip (runs along Z). */
export function verticalRoadPatch(index, config) {
  const offset = getCityOffset(config);
  const span = getCitySpan(config);
  const minX = offset + index * (config.lotSize + config.roadWidth);
  return {
    centerX: minX + config.roadWidth / 2,
    centerZ: offset + span / 2,
    width: config.roadWidth,
    depth: span,
  };
}

/** Horizontal road strip (runs along X). */
export function horizontalRoadPatch(index, config) {
  const offset = getCityOffset(config);
  const span = getCitySpan(config);
  const minZ = offset + index * (config.lotSize + config.roadWidth);
  return {
    centerX: offset + span / 2,
    centerZ: minZ + config.roadWidth / 2,
    width: span,
    depth: config.roadWidth,
  };
}

/** Detail tier from lot distance to city center (for LOD). */
export function lotDetailTier(gx, gz, blockCount) {
  const nx = (gx + 0.5) / blockCount - 0.5;
  const nz = (gz + 0.5) / blockCount - 0.5;
  const dist = Math.sqrt(nx * nx + nz * nz) * 2;
  if (dist < 0.38) {
    return { cellsPerMeter: 7, maxBuildings: 3, shape: 'building' };
  }
  if (dist < 0.72) {
    return { cellsPerMeter: 5, maxBuildings: 2, shape: 'building' };
  }
  return { cellsPerMeter: 3, maxBuildings: 1, shape: 'facade' };
}

export const LOT_TYPE = {
  NORMAL: 0,
  PLAZA: 1,
  ALLEY: 2,
  LARGE: 3,
  TOWER: 4,
};

/** @param {import('../../utils/Random.js').Random} rng */
export function pickLotType(rng) {
  const roll = rng.next();
  if (roll < 0.12) return LOT_TYPE.PLAZA;
  if (roll < 0.2) return LOT_TYPE.ALLEY;
  if (roll < 0.3) return LOT_TYPE.LARGE;
  if (roll < 0.38) return LOT_TYPE.TOWER;
  return LOT_TYPE.NORMAL;
}
